/**
 * <AdminUsersTab />
 *
 * Full user CRUD for the Master Admin:
 *   * GET  /api/admin/users           -- paginated list + role filter
 *   * POST /api/admin/users           -- create (any role)
 *   * PATCH /api/admin/users/:id/role -- change role
 *   * PATCH /api/admin/users/:id       -- toggle active / verified
 *   * DELETE /api/admin/users/:id      -- hard delete
 */
import * as React from "react";
import { Plus, RefreshCcw, Trash2, UserCog } from "lucide-react";
import { api, extractApiError } from "../../../lib/api";
import { Avatar } from "../../../components/ui/Avatar";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { Select } from "../../../components/ui/Select";
import { useAuth } from "../../../contexts/AuthContext";

interface AdminUser {
  id: string;
  email: string;
  username: string;
  full_name?: string | null;
  role: "PARTICIPANT" | "DEALER" | "ADMIN";
  is_active: boolean;
  is_verified: boolean;
  subscription_status: string;
  created_at: string;
}

interface UsersPage {
  items: AdminUser[];
  total: number;
  limit: number;
  skip: number;
}

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "PARTICIPANT", label: "Participant" },
  { value: "DEALER", label: "Dealer" },
  { value: "ADMIN", label: "Admin" },
];

const ROLE_CREATE_OPTIONS = ROLE_OPTIONS.slice(1);

export function AdminUsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = React.useState<AdminUser[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [roleFilter, setRoleFilter] = React.useState<string>("");
  const [q, setQ] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<UsersPage>("/admin/users", {
        params: {
          limit: 100,
          ...(roleFilter ? { role: roleFilter } : {}),
        },
      });
      setUsers(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  React.useEffect(() => {
    load();
  }, [load]);

  const visible = React.useMemo(() => {
    if (!q.trim()) return users;
    const needle = q.trim().toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        u.username.toLowerCase().includes(needle) ||
        (u.full_name ?? "").toLowerCase().includes(needle),
    );
  }, [users, q]);

  const replace = (u: AdminUser) =>
    setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)));

  const remove = (id: string) =>
    setUsers((prev) => prev.filter((x) => x.id !== id));

  return (
    <Card elevated data-testid="admin-users-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <UserCog className="h-5 w-5" aria-hidden /> Users management
          </h2>
          <p className="text-ink-muted text-sm mt-1">
            Create new accounts, change roles, deactivate, or delete. Total:{" "}
            <strong className="text-ink">{total}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            loading={loading}
            leadingIcon={<RefreshCcw className="h-4 w-4" />}
            data-testid="admin-users-refresh"
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCreateOpen(true)}
            leadingIcon={<Plus className="h-4 w-4" />}
            data-testid="admin-users-new"
          >
            New user
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        {/* Filters ---------------------------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3 mb-4">
          <Input
            label="Search"
            hideLabel
            placeholder="Search by email, username, or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-users-search"
          />
          <Select
            label="Filter by role"
            hideLabel
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            options={ROLE_OPTIONS}
            data-testid="admin-users-role-filter"
          />
        </div>

        {error && (
          <p
            className="mb-3 text-sm font-semibold text-brand"
            data-testid="admin-users-error"
          >
            {error}
          </p>
        )}

        {/* List ------------------------------------------------------- */}
        {visible.length === 0 ? (
          <p
            className="text-ink-muted text-center py-8"
            data-testid="admin-users-empty"
          >
            {loading ? "Loading…" : "No users match your filter."}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-surface-border">
            {visible.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isSelf={u.id === me?.id}
                onChange={replace}
                onDelete={remove}
              />
            ))}
          </ul>
        )}
      </CardBody>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(u) => setUsers((prev) => [u, ...prev])}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function roleBadgeVariant(r: string): "solid" | "brand" | "outline" {
  if (r === "ADMIN") return "solid";
  if (r === "DEALER") return "brand";
  return "outline";
}

function UserRow({
  user,
  isSelf,
  onChange,
  onDelete,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChange: (u: AdminUser) => void;
  onDelete: (id: string) => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const setRole = async (role: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.patch<AdminUser>(`/admin/users/${user.id}/role`, {
        role,
        auto_grant_subscription: role === "DEALER",
      });
      onChange(r.data);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.patch<AdminUser>(`/admin/users/${user.id}`, {
        is_active: !user.is_active,
      });
      onChange(r.data);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/admin/users/${user.id}`);
      onDelete(user.id);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <li className="py-4" data-testid={`admin-user-row-${user.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <Avatar name={user.username} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink truncate">
              {user.full_name ?? user.username}
            </span>
            <Badge variant={roleBadgeVariant(user.role)} size="sm">
              {user.role}
            </Badge>
            {!user.is_active && (
              <Badge variant="outline" size="sm">
                Disabled
              </Badge>
            )}
            {isSelf && (
              <Badge variant="soft" size="sm">
                You
              </Badge>
            )}
          </div>
          <div className="text-xs text-ink-muted mt-0.5 truncate">
            @{user.username} · {user.email}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-36">
            <Select
              label="Role"
              hideLabel
              value={user.role}
              onChange={(e) => setRole(e.target.value)}
              options={ROLE_CREATE_OPTIONS}
              disabled={isSelf || busy}
              data-testid={`admin-user-role-${user.id}`}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleActive}
            loading={busy && !confirmDelete}
            disabled={isSelf}
            data-testid={`admin-user-toggle-active-${user.id}`}
          >
            {user.is_active ? "Disable" : "Enable"}
          </Button>
          <Button
            variant={confirmDelete ? "primary" : "outline"}
            size="sm"
            onClick={del}
            loading={busy && confirmDelete}
            disabled={isSelf}
            leadingIcon={<Trash2 className="h-4 w-4" />}
            data-testid={`admin-user-delete-${user.id}`}
          >
            {confirmDelete ? "Click again" : "Delete"}
          </Button>
        </div>
      </div>
      {err && (
        <p
          className="mt-2 text-sm font-semibold text-brand"
          data-testid={`admin-user-error-${user.id}`}
        >
          {err}
        </p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Create user modal
// ---------------------------------------------------------------------------
function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (u: AdminUser) => void;
}) {
  const [form, setForm] = React.useState({
    email: "",
    username: "",
    full_name: "",
    password: "",
    role: "PARTICIPANT",
  });
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<AdminUser>("/admin/users", form);
      onCreated(r.data);
      onClose();
      setForm({
        email: "",
        username: "",
        full_name: "",
        password: "",
        role: "PARTICIPANT",
      });
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a new user"
      description="Admin-created accounts are pre-verified. Dealer accounts skip Stripe and are flagged ADMIN_GRANTED."
      testid="admin-create-user-modal"
    >
      <form onSubmit={submit} className="flex flex-col gap-3" data-testid="admin-create-user-form">
        <Input
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={set("email")}
          data-testid="admin-create-user-email"
        />
        <Input
          label="Username"
          required
          minLength={3}
          maxLength={40}
          value={form.username}
          onChange={set("username")}
          data-testid="admin-create-user-username"
        />
        <Input
          label="Full name (optional)"
          value={form.full_name}
          onChange={set("full_name")}
          data-testid="admin-create-user-fullname"
        />
        <Input
          label="Password"
          type="password"
          required
          minLength={8}
          value={form.password}
          onChange={set("password")}
          hint="At least 8 characters, including a letter and a number."
          data-testid="admin-create-user-password"
        />
        <Select
          label="Role"
          value={form.role}
          onChange={set("role") as any}
          options={ROLE_CREATE_OPTIONS}
          data-testid="admin-create-user-role"
        />
        {err && (
          <p
            className="text-sm font-semibold text-brand"
            data-testid="admin-create-user-error"
          >
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={busy}
            data-testid="admin-create-user-submit"
          >
            Create user
          </Button>
        </div>
      </form>
    </Modal>
  );
}
